// ✅ React & Router
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

// ✅ Firebase - Firestore & Storage
import { db } from "../firebase/firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { getStorage, ref, uploadBytes } from "firebase/storage";

// ✅ Utilities
import { fetchSchoolId } from "../firebase/firestoreUtils";
import { formatDateLong, calculateDaysToGo } from "../utils/dateUtils";
import { format } from "date-fns";

// ✅ Components
import Header from "../components/Header";
import LinearProgress from "../components/LinearProgress";

// ✅ Icons
import { FiHome, FiCopy } from "react-icons/fi";

// ✅ Styles
import "../styles/DonatePage.css";



const DonatePage = () => {
    const { schoolShortName, fundraiserId } = useParams();
    const navigate = useNavigate();
    const [receiptFile, setReceiptFile] = useState(null);
    const [showName, setShowName] = useState(true);
    const [donors, setDonors] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [fundraiser, setFundraiser] = useState(null);
    const [donorName, setDonorName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [amount, setAmount] = useState("");
    const [loading, setLoading] = useState(true);
    const [totalRaised, setTotalRaised] = useState(0);
    const storage = getStorage();
    const [schoolData, setSchoolData] = useState(null);
    const [clubData, setClubData] = useState(null);




    useEffect(() => {
        const fetchAllData = async () => {
            if (!schoolShortName || !fundraiserId) {
                alert("Error: Missing school or fundraiser information.");
                navigate("/");
                return;
            }

            // ✅ 1. Get schoolId
            const id = await fetchSchoolId(schoolShortName);
            if (!id) {
                alert("Error: School not found!");
                navigate("/");
                return;
            }
            setSchoolId(id);

            // ✅ 2. Fetch fundraiser
            const fundraiserRef = doc(db, `schools/${id}/fundraisers/${fundraiserId}`);
            const fundraiserSnap = await getDoc(fundraiserRef);
            if (!fundraiserSnap.exists()) {
                alert("Error: Fundraiser not found!");
                navigate("/");
                return;
            }

            const fundraiserData = fundraiserSnap.data();
            setFundraiser(fundraiserData);

            // ✅ 3. Fetch schoolData
            const schoolRef = doc(db, `schools/${id}`);
            const schoolSnap = await getDoc(schoolRef);
            if (schoolSnap.exists()) {
                setSchoolData({ ...schoolSnap.data(), id });
            }

            // ✅ 4. Fetch clubData (guna club_id dari fundraiser)
            if (fundraiserData.club_id) {
                const clubRef = doc(db, `schools/${id}/clubs/${fundraiserData.club_id}`);
                const clubSnap = await getDoc(clubRef);
                if (clubSnap.exists()) {
                    setClubData({ ...clubSnap.data(), id: clubSnap.id });
                }
            }

            // ✅ 5. Fetch donors & calculate total
            const donorRef = collection(db, `schools/${id}/fundraisers/${fundraiserId}/donors`);
            const snapshot = await getDocs(donorRef);

            let confirmedTotal = 0;
            const donorList = snapshot.docs.map(doc => {
                const data = doc.data();
                if (data.confirmed === "confirmed") {
                    confirmedTotal += Number(data.amount || 0);
                }
                return data;
            });

            setDonors(donorList);
            setTotalRaised(confirmedTotal);
            setLoading(false);
        };

        fetchAllData();
    }, [schoolShortName, fundraiserId, navigate]);




    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!schoolId || !fundraiser) {
            alert("Error: School or fundraiser data is missing!");
            return;
        }

        if (!donorName.trim()) {
            alert("Please enter your name.");
            return;
        }

        if (!phoneNumber.trim()) {
            alert("Please enter your phone number.");
            return;
        }

        const cleanedPhoneNumber = phoneNumber.trim();
        if (!/^\d{9,11}$/.test(cleanedPhoneNumber)) {
            alert("Please enter a valid phone number (e.g., 01123456789).");
            return;
        }

        const formattedPhoneNumber = `+6${cleanedPhoneNumber}`;

        const donationAmount = parseFloat(amount);
        if (isNaN(donationAmount) || donationAmount <= 0) {
            alert("Please enter a valid donation amount.");
            return;
        }

        if (!receiptFile) {
            alert("Please upload your receipt.");
            return;
        }



        // ✅ Get current goal & raised
        const goal = parseFloat(fundraiser.donations[0]?.goal_amount) || 0;

        // Fetch semula jumlah kutipan semasa dari donors
        try {
            const donorRef = collection(db, `schools/${schoolId}/fundraisers/${fundraiserId}/donors`);
            const snapshot = await getDocs(donorRef);
            let currentRaised = 0;
            snapshot.forEach(doc => {
                currentRaised += Number(doc.data().amount || 0);
            });

            // ✅ SEMAK SAMA ADA DAH CUKUP
            if (currentRaised >= goal) {
                alert("Alhamdulillah! Kutipan telah mencukupi. Anda tak perlu menderma lagi.");
                return;
            }

            // Upload receipt to Firebase Storage
            const storageRef = ref(storage, `receipts/${fundraiserId}_${Date.now()}_${receiptFile.name}`);
            await uploadBytes(storageRef, receiptFile);

            const uploadedPath = storageRef.fullPath;


            // ✅ Jika belum cukup, teruskan simpan
            await addDoc(donorRef, {
                name: donorName || "Anonymous",
                phone: formattedPhoneNumber,
                amount: donationAmount,
                fundraiser_id: fundraiserId,
                date: new Date(),
                show_name: showName,
                receipt_filename: uploadedPath


            });

            alert("Thank you for your donation!");
            navigate(`/`);
        } catch (error) {
            console.error("❌ Error processing donation:", error);
            alert("Failed to process donation.");
        }
    };


    if (loading) return <p>Loading donation page...</p>;
    if (!fundraiser) return <p>Error: Fundraiser not found!</p>;
    const goalAmount = parseFloat(fundraiser.donations[0]?.goal_amount || 0);
    const percentage = goalAmount > 0 ? (totalRaised / goalAmount) * 100 : 0;

    return (
        <div className="donate-container">
            <Header schoolData={schoolData} clubData={clubData} />


            <div className="fundraiser-header">
                <h2>{fundraiser.donations[0]?.title || "Untitled Fundraiser"}</h2>

                {/* ✅ Display fundraiser image if available */}
                {fundraiser.donations[0]?.image && (
                    <img src={fundraiser.donations[0].image} alt="Fundraiser" className="fundraiser-image" />
                )}
                <LinearProgress percentage={percentage} />

                {/* ✅ Event Date Explanation */}
                <p><strong>📅 When is it happening?</strong><br />
                    {fundraiser.donations[0]?.event_name} is set for {formatDateLong(fundraiser.donations[0]?.event_date)} — just around the corner!
                </p>

                {/* ✅ Countdown Explanation */}
                <p><strong>⏳ How much time is left?</strong><br />
                    Only <strong>{calculateDaysToGo(fundraiser.donations[0]?.event_date)} days</strong> to go before we take the stage and represent our school!
                </p>

                {/* ✅ Why Donations Are Needed */}
                <p><strong>💰 Why do we need your support?</strong><br />
                    {fundraiser.donations[0]?.note}
                </p>


                {/* ✅ Fundraising Goal & Progress */}
                <p><strong>🎯 How much do we need?</strong><br />
                    Our goal is <strong>RM {fundraiser.donations[0]?.goal_amount + ".00" || "0"}</strong>, and we’ve confirmed receiving <strong>RM {totalRaised.toFixed(2)}</strong> from verified donors.
                </p>

                {/* ✅ Call to Action */}
                <p><strong>🙏 Can you help us cross the finish line?</strong><br />
                    Every donation, big or small, makes a difference. Support our journey today!
                </p>


                {fundraiser.donations[0]?.permission_url && (
                    <p style={{ marginTop: 6 }}>
                        <a href={fundraiser.donations[0]?.permission_url} target="_blank" rel="noopener noreferrer">
                            View Permission Letter
                        </a>
                    </p>
                )}


            </div>

            <form onSubmit={handleSubmit} className="donate-form">

                <div className="bank-transfer-info">
                    <h3>Bank Account Information</h3>
                    <p>Account Name :<br /> <strong>PIBG SK TAMAN PUTRA PERDANA 2</strong></p>
                    <p style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        Bank :<br /><strong>Maybank</strong>
                        <img
                            src={require("../images/maybank.png")}
                            alt="Maybank"
                            style={{ height: "24px" }}
                        />
                    </p>

                    <p>
                        Account Number :
                        <span className="account-number"> <strong>562777210070 </strong></span>
                        <FiCopy
                            onClick={() => {
                                navigator.clipboard.writeText("562777210070");
                                alert("Account number copied!");
                            }}
                            style={{
                                cursor: "pointer",
                                marginLeft: "10px",
                                fontSize: "1.2rem",
                            }}
                            title="Copy account number"
                        />



                    </p>
                </div>


                <label>
                    <input
                        type="checkbox"
                        checked={showName}
                        onChange={(e) => setShowName(e.target.checked)}
                    />
                    Display my name on the donor list
                </label>
                <input type="text" placeholder="Your Name" value={donorName} onChange={(e) => setDonorName(e.target.value)} style={{ fontSize: "16px" }} />

                <input
                    type="tel"
                    placeholder="Your Phone (for record only, e.g. 01123456789)"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    style={{ fontSize: "16px" }}
                />
                <div className="amount-options">
                    <p><strong>Select an amount:</strong></p>
                    <div className="amount-buttons">
                        {[5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000].map((value) => (
                            <button
                                key={value}
                                type="button"
                                className={`amount-button ${amount === value.toString() ? 'selected' : ''}`}
                                onClick={() => setAmount(value.toString())}
                            >
                                RM{value}
                            </button>
                        ))}
                    </div>
                </div>

                <label htmlFor="receiptUpload" ><strong>Upload Receipt</strong></label>
                <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    style={{ fontSize: "16px", margin: "0px" }}
                />


                <p className="small-note">
                    Kindly include the reference <strong>"Robotik SKTPP2"</strong> in your receipt or transfer details to ease verification. This receipt helps us verify the donation with PIBG for proper record and reimbursement purposes.
                </p>





                <div className="button-container">
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <p
                            onClick={() => navigate("/")}
                            style={{
                                cursor: "pointer",
                                color: "#007bff",
                                fontSize: "20px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                margin: "0px 20px"

                            }}
                        >
                            <FiHome /> Home
                        </p>
                    </div>


                    <button type="submit" className="submit-button">Donate</button>
                </div>
            </form>

            <div className="donation-flow">
                <h3>How Your Donation is Processed</h3>
                <ol>
                    <li><strong>You donate:</strong> Choose an amount and fill in your details.</li>
                    <li><strong>Upload your receipt:</strong> This helps us verify your contribution.</li>
                    <li><strong>We verify:</strong> Our team will review your information and confirm your donation.</li>
                    <li><strong>We claim from PIBG:</strong> Verified donations are submitted to PIBG for official recording and reimbursement.</li>
                </ol>
                <p className="small-note">By uploading your receipt and details, you're helping us ensure accountability and smooth financial tracking.</p>
            </div>

            <hr />


            {donors.length > 0 && (
                <div className="donor-list">
                    <h3>Recent Donors</h3>
                    <p>{fundraiser.donations[0]?.title || "Untitled Fundraiser"}</p>

                    <table className="donor-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Name</th>
                                <th>RM</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {donors.map((donor, index) => {
                                const timestamp = donor.date?.toDate?.() || new Date();
                                const formattedDate = format(timestamp, "dd/MM/yy, HH:mm");
                                return (
                                    <tr key={index}>
                                        <td>{formattedDate}</td>
                                        <td>{donor.show_name ? donor.name : "Hamba Allah"}</td>
                                        <td>{Number(donor.amount).toFixed(2)}</td>
                                        <td>
                                            {donor.confirmed === "confirmed"
                                                ? "✅ Confirmed"
                                                : donor.confirmed === "invalid"
                                                    ? "❌ Invalid"
                                                    : "⏳ In Review"}
                                        </td>                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}


        </div>
    );
};

export default DonatePage;


